<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { TrackRecordSourceEnum } from '@/enums/trackRecord_enum.ts'
import type {
  TrackRecordAnalyzeResponse,
  TrackRecordPerfType,
  TrackRecordPerfUnit,
  TrackRecordWizardMapping,
} from '@/types/services/mandate/trackRecord.type.ts'

const props = defineProps<{
  mandateId: number
  source: TrackRecordSourceEnum
  analysis: TrackRecordAnalyzeResponse
  mapping: TrackRecordWizardMapping
  perfType: TrackRecordPerfType | null
  perfUnit: TrackRecordPerfUnit | null
  capitalInitial: number
  replaceExisting: boolean
  loading: boolean
}>()

const emit = defineEmits<{
  (e: 'import'): void
  (e: 'back'): void
}>()

const { t } = useI18n()

function perfTypeLabel(type: TrackRecordPerfType | null): string {
  if (type === 'period') return t('views.trackRecord.confirm.perfTypePeriod')
  if (type === 'cumulative') return t('views.trackRecord.confirm.perfTypeCumulative')
  return '—'
}

function perfUnitLabel(unit: TrackRecordPerfUnit | null): string {
  if (unit === 'decimal') return t('views.trackRecord.confirm.perfUnitDecimal')
  if (unit === 'percent') return t('views.trackRecord.confirm.perfUnitPercent')
  return t('views.trackRecord.confirm.perfUnitAuto')
}
</script>

<template>
  <v-row>
    <v-col cols="12">
      <p class="text-subtitle-1 font-weight-medium mb-4">
        {{ t('views.trackRecord.confirm.title') }}
      </p>

      <v-list density="compact" class="bg-surface-variant rounded">
        <v-list-item>
          <template #prepend>
            <span class="text-medium-emphasis text-body-2 recap-label">
              {{ t('views.trackRecord.confirm.mandate') }}
            </span>
          </template>
          <span class="text-body-2 font-weight-medium">{{ mandateId }}</span>
        </v-list-item>

        <v-divider />

        <v-list-item>
          <template #prepend>
            <span class="text-medium-emphasis text-body-2 recap-label">
              {{ t('views.trackRecord.confirm.source') }}
            </span>
          </template>
          <span class="text-body-2 font-weight-medium">{{ source }}</span>
        </v-list-item>

        <v-divider />

        <v-list-item>
          <template #prepend>
            <span class="text-medium-emphasis text-body-2 recap-label">
              {{ t('views.trackRecord.confirm.dataType') }}
            </span>
          </template>
          <span class="text-body-2 font-weight-medium">
            {{
              mapping.mode === 'value'
                ? t('views.trackRecord.confirm.dataTypeValue')
                : t('views.trackRecord.confirm.dataTypePerf')
            }}
          </span>
        </v-list-item>

        <v-divider />

        <v-list-item>
          <template #prepend>
            <span class="text-medium-emphasis text-body-2 recap-label">
              {{ t('views.trackRecord.confirm.dateColumn') }}
            </span>
          </template>
          <span class="text-body-2 font-weight-medium">{{ mapping.date_column }}</span>
        </v-list-item>

        <v-divider />

        <v-list-item v-if="mapping.mode === 'value'">
          <template #prepend>
            <span class="text-medium-emphasis text-body-2 recap-label">
              {{ t('views.trackRecord.confirm.valueColumn') }}
            </span>
          </template>
          <span class="text-body-2 font-weight-medium">{{ mapping.value_column }}</span>
        </v-list-item>

        <template v-if="mapping.mode === 'perf'">
          <v-list-item>
            <template #prepend>
              <span class="text-medium-emphasis text-body-2 recap-label">
                {{ t('views.trackRecord.confirm.perfColumn') }}
              </span>
            </template>
            <span class="text-body-2 font-weight-medium">{{ mapping.perf_column }}</span>
          </v-list-item>

          <v-divider />

          <v-list-item>
            <template #prepend>
              <span class="text-medium-emphasis text-body-2 recap-label">
                {{ t('views.trackRecord.confirm.perfType') }}
              </span>
            </template>
            <span class="text-body-2 font-weight-medium">{{ perfTypeLabel(perfType) }}</span>
          </v-list-item>

          <v-divider />

          <v-list-item>
            <template #prepend>
              <span class="text-medium-emphasis text-body-2 recap-label">
                {{ t('views.trackRecord.confirm.perfUnit') }}
              </span>
            </template>
            <span class="text-body-2 font-weight-medium">{{ perfUnitLabel(perfUnit) }}</span>
          </v-list-item>

          <v-divider />

          <v-list-item>
            <template #prepend>
              <span class="text-medium-emphasis text-body-2 recap-label">
                {{ t('views.trackRecord.confirm.capitalInitial') }}
              </span>
            </template>
            <span class="text-body-2 font-weight-medium">{{ capitalInitial }}</span>
          </v-list-item>
        </template>

        <v-divider />

        <v-list-item>
          <template #prepend>
            <span class="text-medium-emphasis text-body-2 recap-label">
              {{ t('views.trackRecord.confirm.rowCount') }}
            </span>
          </template>
          <span class="text-body-2 font-weight-medium">{{ analysis.row_count }}</span>
        </v-list-item>

        <v-divider />

        <v-list-item>
          <template #prepend>
            <span class="text-medium-emphasis text-body-2 recap-label">
              {{ t('views.trackRecord.confirm.replaceExisting') }}
            </span>
          </template>
          <v-chip
            :color="replaceExisting ? 'warning' : 'default'"
            size="x-small"
          >
            {{ replaceExisting ? t('views.trackRecord.confirm.yes') : t('views.trackRecord.confirm.no') }}
          </v-chip>
        </v-list-item>
      </v-list>
    </v-col>

    <v-col cols="12" class="d-flex justify-space-between">
      <v-btn variant="text" :disabled="loading" @click="emit('back')">
        <template #prepend>
          <v-icon icon="PhArrowLeft" />
        </template>
        {{ t('views.trackRecord.buttons.back') }}
      </v-btn>

      <v-btn
        color="primary"
        :loading="loading"
        @click="emit('import')"
      >
        {{ t('views.trackRecord.confirm.importButton') }}
        <template #append>
          <v-icon icon="PhUpload" />
        </template>
      </v-btn>
    </v-col>
  </v-row>
</template>

<style scoped>
.recap-label {
  min-width: 200px;
  display: inline-block;
}
</style>
